// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — keeping the globes of a 2- or 4-globe layout on one date when
 * any of them is a stream.
 *
 * The multi-globe sync in `main.ts` (`attachPrimaryVideoSync`,
 * `correctSiblingDrift`) was written for catalogue datasets, which
 * declare a start and an end and spread their video evenly between
 * them (`utils/time.ts`). A stream (`R2_DASH_…`) declares neither: its
 * dates are the frames of its `.dsa` time axis. With two streams the
 * sync found no date on either side and left every other globe where
 * it was, so after a seek one globe read 2070 and the next 1950.
 *
 * Each panel's dates are now one of two kinds:
 *
 *  - `range`: a catalogue dataset, mapped exactly as before
 *    (`videoTimeToDate` / `dateToVideoTime` / the pacing ratio of
 *    `computeSiblingSyncCorrection`). When both panels are `range`, the
 *    decision *is* `computeSiblingSyncCorrection`'s, so catalogue
 *    behaviour does not change.
 *  - `frames`: a stream, mapped through its frame calendar
 *    (`frameCalendar.ts`) on the periods its frames' labels name. The
 *    primary's date is a point running smoothly through its frames; a
 *    stream that follows it is moved to the frame whose period holds that
 *    date, so a monthly globe beside an annual one walks January to
 *    December through each year, an annual one beside a monthly one
 *    stays on the year, and two of the same cadence show the same label.
 *
 * A date outside a stream's periods pins it to its first or last frame
 * and marks it out of range, as a catalogue dataset's boundary is.
 *
 * Pure: the caller reads the elements and applies the decision.
 */

import {
  periodFrameFloat,
  periodPosition,
  periodStartMs,
  timeAtPeriodFrameFloat,
  frameTimeMs,
  type FrameAxis,
  type PeriodPosition,
} from './frameCalendar'
import { timelineAxis, type DsaTimeline } from './dsaTimeline'
import {
  MAX_PLAYBACK_RATE,
  MIN_PLAYBACK_RATE,
  SIBLING_HARD_SEEK_THRESHOLD_S,
  SIBLING_SEEK_EPS_S,
  computeSiblingSyncCorrection,
  dateToVideoTime,
  videoTimeToDate,
  type SiblingSyncCorrection,
} from '../utils/time'

/** How one panel's video time maps to dates. */
export type PanelDates =
  | {
      readonly kind: 'range'
      readonly startMs: number
      readonly endMs: number
      /** The video's duration, seconds (live: it firms up as a stream buffers). */
      readonly duration: number
    }
  | {
      readonly kind: 'frames'
      readonly axis: FrameAxis
      /** Encoded video frames per second: one data frame each. */
      readonly fps: number
    }

/** A catalogue dataset's dates, or null when the range or the duration cannot map anything. */
export function rangePanelDates(start: string | Date, end: string | Date, duration: number): PanelDates | null {
  const startMs = new Date(start).getTime()
  const endMs = new Date(end).getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || !(duration > 0)) return null
  return { kind: 'range', startMs, endMs, duration }
}

/** A stream's dates from its `.dsa` time axis. */
export function framePanelDates(timeline: DsaTimeline): PanelDates | null {
  if (!(timeline.videoFrameRate > 0) || !(timeline.frameCount > 0)) return null
  return { kind: 'frames', axis: timelineAxis(timeline), fps: timeline.videoFrameRate }
}

/** Frame position (fractional) of a video time, held inside the axis so its end is not "after". */
function framePosition(p: Extract<PanelDates, { kind: 'frames' }>, videoS: number): number {
  const f = (Number.isFinite(videoS) ? Math.max(0, videoS) : 0) * p.fps
  return Math.min(f, p.axis.frameCount - 1e-6)
}

/** The data frame a video time shows (`floor(t × fps)`, clamped), as `dsaTimeline.frameAtVideoTime`. */
export function panelFrameAt(p: Extract<PanelDates, { kind: 'frames' }>, videoS: number): number {
  return Math.floor(framePosition(p, videoS))
}

/**
 * Whether a stream is already on the frame a target time is in, and at
 * least a tenth of a frame clear of either edge of it (where a decoder
 * may resolve to the neighbour). A seek from there would change nothing
 * on screen.
 */
export function isOnFrame(p: Extract<PanelDates, { kind: 'frames' }>, currentS: number, targetS: number): boolean {
  if (!Number.isFinite(currentS)) return false
  const f = currentS * p.fps
  const frame = Math.floor(f)
  if (frame !== panelFrameAt(p, targetS) || frame >= p.axis.frameCount) return false
  const into = f - frame
  return into >= 0.1 && into <= 0.9
}

/**
 * The date a panel shows at a video time, running smoothly between
 * frames: the master clock the other globes follow. Not snapped to the
 * label, so a follower paced on it moves evenly instead of in steps.
 */
export function panelDateAt(p: PanelDates, videoS: number): number {
  if (p.kind === 'range') {
    return videoTimeToDate(videoS, p.duration, new Date(p.startMs), new Date(p.endMs)).getTime()
  }
  return timeAtPeriodFrameFloat(p.axis, framePosition(p, videoS))
}

/** Mean data milliseconds per second of video: the pacing ratio's two sides. */
export function panelMsPerSecond(p: PanelDates): number {
  if (p.kind === 'range') return p.duration > 0 ? (p.endMs - p.startMs) / p.duration : 0
  const span = periodStartMs(p.axis, p.axis.frameCount) - periodStartMs(p.axis, 0)
  return span > 0 ? (span * p.fps) / p.axis.frameCount : 0
}

export interface PanelLocation {
  readonly position: PeriodPosition
  /** Video time, seconds, that shows the date (its boundary frame when outside). */
  readonly videoTime: number
}

/**
 * Where a panel shows a date. `frame` (a seek, a pause, a play): on a
 * stream, the middle of the frame whose period holds the date, so the
 * decoder cannot resolve to the frame either side of a cut. `smooth`
 * (following playback): the same frame, at the point through it the
 * date has reached, so a follower's playhead advances evenly. A
 * catalogue dataset has one answer for both: `dateToVideoTime`.
 */
export function panelLocate(p: PanelDates, dateMs: number, mode: 'frame' | 'smooth'): PanelLocation {
  if (p.kind === 'range') {
    const { videoTime, position } = dateToVideoTime(new Date(dateMs), p.duration, new Date(p.startMs), new Date(p.endMs))
    return { videoTime, position }
  }
  const n = p.axis.frameCount
  const position = periodPosition(p.axis, dateMs)
  if (position === 'before') return { position, videoTime: 0.5 / p.fps }
  if (position === 'after') return { position, videoTime: (n - 0.5) / p.fps }
  const f = Math.min(Math.max(periodFrameFloat(p.axis, dateMs), 0), n - 1e-6)
  return { position, videoTime: mode === 'frame' ? (Math.floor(f) + 0.5) / p.fps : f / p.fps }
}

/**
 * Drift, in video seconds, past which a playing stream that follows
 * another is seeked rather than eased. The catalogue's 0.15 s
 * ({@link SIBLING_HARD_SEEK_THRESHOLD_S}) is under one frame at the
 * 6 fps the annual streams are encoded at, but nearly two at 12 fps; a
 * follower is meant to stay within one frame of the date, so the
 * threshold is nine tenths of a frame, never above 0.15 s, never below
 * 0.06 s (twice the drift the rate trim holds in steady state, so it
 * does not seek continuously).
 */
export function framesHardSeekThresholdS(fps: number): number {
  if (!(fps > 0)) return SIBLING_HARD_SEEK_THRESHOLD_S
  return Math.min(SIBLING_HARD_SEEK_THRESHOLD_S, Math.max(0.06, 0.9 / fps))
}

/**
 * The fewest milliseconds between two corrective seeks of a playing
 * stream that follows another. A seek on a DASH stream flushes its
 * decoder and refetches a segment; two in quick succession are the
 * stall the first was meant to end. Catalogue siblings are not held to
 * it (their behaviour is unchanged).
 */
export const FRAMES_SEEK_COOLDOWN_MS = 500

const clampRate = (r: number) => Math.max(MIN_PLAYBACK_RATE, Math.min(MAX_PLAYBACK_RATE, r))

/** Rate trim per second of drift, and its cap: `computeSiblingSyncCorrection`'s. */
const SYNC_RATE_GAIN = 0.5
const SYNC_MAX_RATE_TRIM = 0.25

/**
 * The playback rate that moves a sibling through dates at the primary's
 * pace (before any trim), scaled by the primary's own rate. 1 when
 * either side cannot say.
 */
export function pacingRate(primary: PanelDates, sibling: PanelDates, primaryPlaybackRate = 1): number {
  const p = panelMsPerSecond(primary)
  const s = panelMsPerSecond(sibling)
  return clampRate((p > 0 && s > 0 ? p / s : 1) * primaryPlaybackRate)
}

/**
 * One sibling's correction while the primary plays: where it should be,
 * at what rate, and whether to seek now. Two catalogue panels get
 * exactly `computeSiblingSyncCorrection`. Otherwise the same control
 * law on the date mapping above: ease small drift out through the rate,
 * seek past the threshold, pin an out-of-range sibling to its boundary.
 */
export function computePanelSyncCorrection(params: {
  dateMs: number
  primary: PanelDates
  sibling: PanelDates
  sibCurrentTime: number
  primaryPlaybackRate?: number
  /** Overrides the default threshold (catalogue: 0.15 s; stream: {@link framesHardSeekThresholdS}). */
  hardSeekThresholdS?: number
}): SiblingSyncCorrection {
  const { dateMs, primary, sibling, sibCurrentTime, primaryPlaybackRate = 1 } = params
  if (primary.kind === 'range' && sibling.kind === 'range') {
    return computeSiblingSyncCorrection({
      date: new Date(dateMs),
      sibCurrentTime,
      sibDuration: sibling.duration,
      sibStart: new Date(sibling.startMs),
      sibEnd: new Date(sibling.endMs),
      primaryDuration: primary.duration,
      primaryRangeMs: primary.endMs - primary.startMs,
      hardSeekThresholdS: params.hardSeekThresholdS ?? SIBLING_HARD_SEEK_THRESHOLD_S,
      primaryPlaybackRate,
    })
  }
  const threshold = params.hardSeekThresholdS
    ?? (sibling.kind === 'frames' ? framesHardSeekThresholdS(sibling.fps) : SIBLING_HARD_SEEK_THRESHOLD_S)
  const base = pacingRate(primary, sibling, primaryPlaybackRate)
  if (sibling.kind === 'frames') {
    const pos = periodPosition(sibling.axis, dateMs)
    if (pos !== 'inside') {
      const { videoTime } = panelLocate(sibling, dateMs, 'frame')
      // Pinned: on the boundary frame already counts, wherever inside it.
      const onFrame = panelFrameAt(sibling, sibCurrentTime) === panelFrameAt(sibling, videoTime)
      return { position: pos, targetTime: videoTime, rate: base, shouldSeek: !onFrame }
    }
  }
  const { position, videoTime: targetTime } = panelLocate(sibling, dateMs, 'smooth')
  if (position !== 'inside') {
    return { position, targetTime, rate: base, shouldSeek: Math.abs(sibCurrentTime - targetTime) > SIBLING_SEEK_EPS_S }
  }
  const error = sibCurrentTime - targetTime
  if (Math.abs(error) > threshold) return { position, targetTime, rate: base, shouldSeek: true }
  const trim = Math.max(-SYNC_MAX_RATE_TRIM, Math.min(SYNC_MAX_RATE_TRIM, error * SYNC_RATE_GAIN))
  return { position, targetTime, rate: clampRate(base * (1 - trim)), shouldSeek: false }
}

/** How a stream sibling's shown frame compares with the primary's labelled frame or date. */
export interface FramesSiblingVerdict {
  readonly alignment: 'aligned' | 'off' | 'uncovered'
  /** Stamp of the frame the sibling shows, for its notice. */
  readonly shownMs: number
}

/**
 * Does a stream sibling show the date the primary is labelled with?
 * Aligned when the period of the frame it shows overlaps the primary's:
 * the primary's frame period on a stream, its instant on a catalogue
 * dataset. Periods, not instants, because the sibling is placed on the
 * frame holding the primary's running date, which lies anywhere inside
 * the primary's labelled period (an annual primary paused in "1990"
 * may hold a monthly sibling on any month of 1990, and both are right).
 * `uncovered` when that date is outside the sibling's frames: the
 * out-of-range treatment already explains the panel.
 */
export function verifyFramesSibling(params: {
  primary: PanelDates
  primaryTime: number
  /**
   * AYNI (multi-globe plan): the date the sibling is meant to show when
   * it is not the primary's own (its time rule moved it). Checked as an
   * instant: the sibling's frame must hold it.
   */
  dateMs?: number
  sibling: Extract<PanelDates, { kind: 'frames' }>
  /** Playhead of the frame the sibling is showing (`shownFrameTime`). */
  sibFrameTime: number
}): FramesSiblingVerdict {
  const { primary, primaryTime, sibling, sibFrameTime } = params
  const j = panelFrameAt(sibling, sibFrameTime)
  const shownMs = frameTimeMs(sibling.axis, j)
  const shifted = typeof params.dateMs === 'number'
  const dateMs = shifted ? params.dateMs! : panelDateAt(primary, primaryTime)
  if (periodPosition(sibling.axis, dateMs) !== 'inside') return { alignment: 'uncovered', shownMs }
  let from = dateMs
  let to = dateMs
  if (primary.kind === 'frames' && !shifted) {
    const i = panelFrameAt(primary, primaryTime)
    from = periodStartMs(primary.axis, i)
    to = periodStartMs(primary.axis, i + 1)
  }
  const a = periodStartMs(sibling.axis, j)
  const b = periodStartMs(sibling.axis, j + 1)
  // Half-open periods overlap when each starts before the other ends; an
  // instant (from === to) must lie inside [a, b).
  const overlaps = from === to ? from >= a && from < b : a < to && from < b
  return { alignment: overlaps ? 'aligned' : 'off', shownMs }
}
